import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Bookmark,
  Check,
  Library,
  Plus,
  Save,
  Sparkles,
  Trash2
} from 'lucide-react';
import { generatePrayerGuide } from '../services/apiService';
import { deleteSavedGuide, getSavedGuides, savePrayerGuide } from '../services/storageService';
import AudioPrayer from './AudioPrayer';
import ErrorDisplay from './ErrorDisplay';
import PrayerGuideForm from './PrayerGuideForm';
import PrayerGuideView from './PrayerGuideView';
import ScreenLayout from './ScreenLayout';

const quickThemes = [
  { theme: 'Peace', focus: 'an anxious heart' },
  { theme: 'Guidance', focus: 'an important decision' },
  { theme: 'Strength', focus: 'a difficult season' },
  { theme: 'Gratitude', focus: 'noticing God’s goodness' }
];

const firstDayNumber = (guide) => guide?.days?.[0]?.day ?? guide?.days?.[0]?.dayNumber ?? null;

const PrayerGuideGenerator = ({ initialGuide }) => {
  const [theme, setTheme] = useState('');
  const [focus, setFocus] = useState('');
  const [duration, setDuration] = useState(7);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [generatedGuide, setGeneratedGuide] = useState(initialGuide || null);
  const [activeDay, setActiveDay] = useState(firstDayNumber(initialGuide));
  const [savedGuides, setSavedGuides] = useState(() => getSavedGuides().slice().reverse());
  const [showLibrary, setShowLibrary] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (generatedGuide?.days?.length && !activeDay) {
      setActiveDay(firstDayNumber(generatedGuide));
    }
  }, [activeDay, generatedGuide]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsGenerating(true);

    try {
      const guideData = await generatePrayerGuide({ theme, focus, duration, additionalInfo });
      setGeneratedGuide(guideData);
      setActiveDay(firstDayNumber(guideData));
      setShowLibrary(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (generationError) {
      console.error('Error generating prayer guide:', generationError);
      setError(generationError);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedGuide) return;

    setIsSaving(true);
    try {
      const savedGuide = await savePrayerGuide(generatedGuide);
      setSavedGuides((guides) => [savedGuide, ...guides]);
      setSaveSuccess(true);
      window.setTimeout(() => setSaveSuccess(false), 3000);
    } catch (saveError) {
      setError(saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (savedAt) => {
    try {
      await deleteSavedGuide(savedAt);
      setSavedGuides((guides) => guides.filter((guide) => guide.savedAt !== savedAt));
    } catch (deleteError) {
      setError(deleteError);
    }
  };

  const openSavedGuide = (guide) => {
    setGeneratedGuide(guide);
    setActiveDay(firstDayNumber(guide));
    setShowLibrary(false);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const createNewGuide = () => {
    setGeneratedGuide(null);
    setActiveDay(null);
    setShowLibrary(false);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <ScreenLayout title="Prayer Generator" eyebrow="Personal guidance" icon={Sparkles} tone="plum">
      <div className="guide-builder-page">
        <section className="guide-builder-hero">
          <div className="guide-builder-hero-copy">
            <p className="guide-builder-kicker"><Sparkles size={14} /> Prayer shaped around your season</p>
            <h1>A guide for what is on your heart.</h1>
            <p>Create a focused path of scripture, reflection, and prayer for the days ahead.</p>
            <div className="guide-builder-hero-actions">
              <button type="button" onClick={createNewGuide}><Plus size={17} /> New guide</button>
              <button type="button" onClick={() => setShowLibrary(!showLibrary)}>
                <Library size={17} /> Saved guides <span>{savedGuides.length}</span>
              </button>
            </div>
          </div>
          <div className="guide-builder-hero-art" aria-hidden="true">
            <span className="guide-builder-hero-sun"></span>
            <span className="guide-builder-hero-path guide-builder-hero-path-one"></span>
            <span className="guide-builder-hero-path guide-builder-hero-path-two"></span>
            <div>
              <small>Each guide includes</small>
              <strong>Scripture</strong>
              <strong>Reflection</strong>
              <strong>Prayer</strong>
            </div>
          </div>
        </section>

        {error && <ErrorDisplay error={error} />}

        {saveSuccess && (
          <div className="guide-builder-success" role="status">
            <Check size={18} />
            Prayer guide saved to your library.
          </div>
        )}

        {showLibrary ? (
          <section className="guide-library-section" aria-labelledby="saved-guides-heading">
            <div className="guide-section-heading">
              <div>
                <p className="guide-builder-kicker">Return to a path</p>
                <h2 id="saved-guides-heading">Saved prayer guides</h2>
              </div>
              <button type="button" onClick={createNewGuide}><Plus size={17} /> Create another</button>
            </div>

            {savedGuides.length > 0 ? (
              <div className="guide-library-grid">
                {savedGuides.map((guide, index) => (
                  <article className="guide-library-card" key={`${guide.savedAt}-${index}`}>
                    <div>
                      <span>{guide.days?.length || 0} days</span>
                      <Bookmark size={18} />
                    </div>
                    <h3>{guide.title || guide.theme || 'Personalized Prayer Guide'}</h3>
                    <p>{guide.introduction || 'A saved path of scripture, reflection, and prayer.'}</p>
                    <footer>
                      <button type="button" onClick={() => openSavedGuide(guide)}>
                        Open guide <ArrowRight size={16} />
                      </button>
                      <button type="button" onClick={() => handleDelete(guide.savedAt)} aria-label={`Delete ${guide.title || 'prayer guide'}`}>
                        <Trash2 size={16} />
                      </button>
                    </footer>
                  </article>
                ))}
              </div>
            ) : (
              <div className="guide-library-empty">
                <Bookmark size={28} />
                <h3>No saved guides yet</h3>
                <p>Save a generated guide and it will be waiting here when you return.</p>
              </div>
            )}
          </section>
        ) : generatedGuide ? (
          <>
            <section className="guide-reader-section">
              <div className="guide-reader-actions">
                <button type="button" onClick={handleSave} disabled={isSaving}>
                  <Save size={17} /> {isSaving ? 'Saving...' : 'Save guide'}
                </button>
                <button type="button" onClick={createNewGuide}><Plus size={17} /> New guide</button>
              </div>

              <PrayerGuideView
                guide={generatedGuide}
                activeDay={activeDay}
                setActiveDay={setActiveDay}
              />
            </section>

            {activeDay && (
              <section className="guide-audio-section">
                <div>
                  <p className="guide-builder-kicker">Listen & pray</p>
                  <h2>Hear today’s prayer aloud.</h2>
                  <p>Use the guided audio when you need help slowing down and beginning.</p>
                </div>
                <AudioPrayer guide={generatedGuide} day={activeDay} />
              </section>
            )}
          </>
        ) : (
          <section className="guide-builder-workspace">
            <div className="guide-builder-form-panel">
              <div>
                <p className="guide-builder-kicker">Begin your guide</p>
                <h2>What would you like to bring before God?</h2>
              </div>
              <PrayerGuideForm
                theme={theme}
                setTheme={setTheme}
                focus={focus}
                setFocus={setFocus}
                duration={duration}
                setDuration={setDuration}
                additionalInfo={additionalInfo}
                setAdditionalInfo={setAdditionalInfo}
                handleSubmit={handleSubmit}
                isGenerating={isGenerating}
              />
            </div>

            <aside className="guide-builder-aside">
              <p className="guide-builder-kicker">A quick beginning</p>
              <h2>Choose a direction</h2>
              <p>Select one of these common starting points, then make it your own.</p>
              <div>
                {quickThemes.map((item) => (
                  <button
                    type="button"
                    key={item.theme}
                    onClick={() => {
                      setTheme(item.theme);
                      setFocus(item.focus);
                    }}
                  >
                    <span>{item.theme}</span>
                    <small>{item.focus}</small>
                    <ArrowRight size={16} />
                  </button>
                ))}
              </div>
              <blockquote>“Draw near to God, and he will draw near to you.”</blockquote>
              <cite>James 4:8</cite>
            </aside>
          </section>
        )}
      </div>
    </ScreenLayout>
  );
};

export default PrayerGuideGenerator;
